import React, { useState } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, ChevronDown, Search as SearchIcon } from "lucide-react";
import { Modal, Form } from "react-bootstrap"; // Import Modal and Form
import '../../styles/BillingTable.css';
import { Link } from "react-router-dom";
import ResultsPagination from "../ResultsPagination";
import { useEffect } from "react";
import axios from "axios";
import { Eye, MoreVertical, Pencil, Trash2 } from "lucide-react";

const SortIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "5px", opacity: 0.45, verticalAlign: "middle" }}>
    <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
  </svg>
);

export default function BillingTable() {
  const navigate = useNavigate();

  const [timeRange, setTimeRange] = useState("3mo");
  const [openMenuId, setOpenMenuId] = useState(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);

  const [filters, setFilters] = useState({
    status: "All",
    customer: "All",
    plan: "All",
    search: "",
    startDate: "",
    endDate: "",
    timeRange: "3mo"
  });

  const [sortColumn, setSortColumn] = useState("invoice_number");
  const [sortDirection, setSortDirection] = useState("asc");

  // Close the actions dropdown on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (openMenuId !== null) {
        setOpenMenuId(null);
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuId]);


  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
  const [newInvoiceData, setNewInvoiceData] = useState({
    user_id: '',
    plan_id: '',
    amount: '',
    due_date: '',
    // status: 'pending' // ← FIXED: Changed from 'Paid' to 'paid'
  });

  // State for Edit Invoice Modal - FIXED: removed duplicate due_date
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInvoiceData, setEditInvoiceData] = useState({
    id: null,
    amount: '',
    due_date: '', // ← FIXED: Removed duplicate
    status: 'pending'
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  // 🔥 FETCH BILLING
  const fetchBilling = async () => {
    setLoading(true);

    try {
      if (
        (filters.startDate && !filters.endDate) ||
        (!filters.startDate && filters.endDate)
      ) {
        setData([]);
        setTotal(0);
        setTotalPages(1);
        setLoading(false);
        return;
      }

      const params = {
        ...filters,
        page: activePage,
        limit: perPage,
        sortColumn,
        sortDirection,
        _t: Date.now()
      };

      if (!filters.timeRange) {
        delete params.timeRange;
      }

      const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/billing`, {
        params,
        withCredentials: true
      });

      setData(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);

    } catch (err) {
      console.error("Billing fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 FETCH DROPDOWN DATA
  const fetchFilters = async () => {
    try {
      const [custRes, planRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/customers`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/plans`, { withCredentials: true })
      ]);

      setCustomers(Array.isArray(custRes.data) ? custRes.data : custRes.data?.customers || custRes.data?.data || []);
      setPlans(Array.isArray(planRes.data) ? planRes.data : planRes.data?.data || []);

    } catch (err) {
      console.error("Filter fetch error:", err);
    }
  };

  // 🔥 LOAD INITIAL FILTER DATA
  useEffect(() => {
    fetchFilters();
  }, []);

  // 🔥 FETCH BILLING (WITH DEBOUNCE)
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchBilling();
    }, 400);

    return () => clearTimeout(delay);
  }, [filters, activePage, perPage]);

  // 🔥 CLOSE MENU ON OUTSIDE CLICK
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".bt-dots-btn") || event.target.closest(".bt-dropdown-menu")) {
        return;
      }
      setOpenMenuId(null);
    };

    if (openMenuId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  // 🔥 RESET FILTERS
  const resetFilters = () => {
    setFilters({
      status: "All",
      customer: "All",
      plan: "All",
      search: "",
      startDate: "",
      endDate: "",
      timeRange: "3mo"
    });

    setTimeRange("3mo");
    setActivePage(1);
    setPerPage(10);
    setOpenMenuId(null);
  };

  // 🔥 SORTING HANDLER
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setActivePage(1);
  };

  // 🔥 ADD INVOICE HANDLER (to open modal) - FIXED: lowercase 'paid'
  const handleAddInvoiceClick = () => {
    setNewInvoiceData({
      user_id: '',
      plan_id: '',
      amount: '',
      due_date: '',
   // status: 'pending' // ← FIXED: Changed from 'Paid' to 'paid'
    });
    setShowAddInvoiceModal(true);
  };

  const handleNewInvoiceChange = (e) => {
    const { name, value } = e.target;
    setNewInvoiceData(prev => ({ ...prev, [name]: value }));
  };

const handleNewInvoiceSubmit = async (e) => {
  e.preventDefault();

  const { user_id, plan_id, amount, due_date } = newInvoiceData; // ✅ Removed 'status'

  if (!user_id || !plan_id || !amount || !due_date) {
    alert("Please fill all required fields: Customer, Plan, Amount, and Due Date.");
    return;
  }

  if (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
    alert("Amount must be a positive number.");
    return;
  }

  try {
    await axios.post(`${process.env.REACT_APP_BASE_API_URL}/api/billing`, {
      user_id,
      plan_id: parseInt(plan_id),
      amount: parseFloat(amount),
      due_date
      // ✅ status is NOT sent - backend will set it to 'pending'
    }, { withCredentials: true });

    alert("Invoice added successfully!");
    setShowAddInvoiceModal(false);
    fetchBilling();
  } catch (err) {
    console.error("Add invoice failed:", err);
    alert(err.response?.data?.error || "Failed to add invoice.");
  }
};

  const handleView = (row) => {
    if (!row.id) {
      alert("This customer has no invoice to view.");
      return;
    }
    console.log("View invoice:", row);
    window.open(`${process.env.REACT_APP_BASE_API_URL}/api/billing/${row.id}/view`, '_blank');
  };

  // 🔥 ADD INVOICE HANDLER
  const handleAddInvoice = async () => {
    const customerName = prompt("Enter Customer Name for the new invoice:");
    if (!customerName) return;

    const customer = customers.find(c => (c.fullName || c.full_name || c.name) === customerName);
    if (!customer) {
      alert("Customer not found. Please enter an existing customer name.");
      return;
    }
    const user_id = customer._id || customer.id;

    const amountStr = prompt("Enter Amount for the new invoice:");
    if (!amountStr) return;
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount <= 0) {
      alert("Invalid amount. Please enter a positive number.");
      return;
    }

    const dueDate = prompt("Enter Due Date (YYYY-MM-DD) for the new invoice:");
    if (!dueDate) return;

    try {
      await axios.post(`${process.env.REACT_APP_BASE_API_URL}/api/billing`, {
        user_id, amount, due_date: dueDate
      }, { withCredentials: true });
      alert("Invoice added successfully!");
      fetchBilling();
    } catch (err) {
      console.error("Add invoice failed:", err);
      alert(err.response?.data?.error || "Failed to add invoice.");
    }
  };

  const handleEdit = (row) => {
    if (!row.id) {
      alert("This customer has no invoice to edit.");
      return;
    }

    setEditInvoiceData({
      id: row.id,
      amount: row.amount || '',
      due_date: row.due_date?.split("T")[0] || '',
      status: row.status || 'pending'
    });

    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const handleEditInvoiceChange = (e) => {
    const { name, value } = e.target;
    setEditInvoiceData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditInvoiceSubmit = async (e) => {
    e.preventDefault();

    const { id, amount, due_date, status } = editInvoiceData;

    if (!amount || !due_date || !status) {
      alert("Please fill all fields: Amount, Due Date, and Status.");
      return;
    }

    if (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      alert("Amount must be a positive number.");
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_API_URL}/api/billing/${id}`,
        {
          amount: parseFloat(amount),
          due_date,
          status: status.toLowerCase() // ← FIXED: Ensure lowercase status
        },
        {
          withCredentials: true
        }
      );

      setData(prevData => prevData.map(item => {
        if (item.id === id || String(item.id) === String(id)) {
          return { ...item, amount: parseFloat(amount), due_date, status };
        }
        return item;
      }));

      setShowEditModal(false);
      alert("Invoice updated successfully");

    } catch (err) {
      console.error(err);
      alert("Failed to update invoice");
    }
  };

  const handleDelete = (id) => {
    if (!id) {
      alert("This customer has no invoice to delete.");
      return;
    }
    setInvoiceToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    console.log("Delete invoice:", invoiceToDelete);
    try {
      await axios.delete(`${process.env.REACT_APP_BASE_API_URL}/api/billing/${invoiceToDelete}`, { withCredentials: true });
      alert("Invoice deleted successfully!");
      fetchBilling();
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
    } catch (err) {
      console.error("Delete invoice failed:", err);
      alert("Failed to delete invoice.");
    }
  };

  const handleDownloadInvoice = (row) => {
    if (!row.id) {
      alert("This customer has no invoice to download.");
      return;
    }
    window.open(`${process.env.REACT_APP_BASE_API_URL}/api/billing/${row.id}/download`, '_blank');
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  // 🔥 PAGINATION CALCULATION
  const start = (activePage - 1) * perPage + 1;
  const end = Math.min(activePage * perPage, total);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <nav
        aria-label="breadcrumb"
        style={{
          "--bs-breadcrumb-divider": "''",
        }}
      >
        <ol
          className="breadcrumb d-flex align-items-center py-2 mb-0"
          style={{ fontSize: "14px" }}
        >
          <li className="breadcrumb-item d-flex align-items-center">
            <Link to="/" className="text-decoration-none text-secondary">
              Home
            </Link>
            <ChevronRight size={16} className="mx-1 text-secondary" />
          </li>

          <li
            className="breadcrumb-item fw-semibold"
            style={{ color: "#2F7A87" }}
          >
            Billing Table
          </li>
        </ol>
      </nav>
      <div className="bt-page">
        <div className="bt-header-row">
          <div>
            <h1 className="bt-title">Billing Table</h1>
            <p className="bt-subtitle">View assigned queries, track leads, and manage sales activities in one place.</p>
          </div>
          <button
            className="btn btn-primary ms-auto"
            onClick={handleAddInvoiceClick}
            style={{ backgroundColor: "#2F7F8F", border: "none", borderRadius: "50px", padding: "8px 15px", fontSize: "14px", color: "#fff" }}
          >
            + Add New Invoice
          </button>
        </div>

        {/* Search Input */}
        <div className="bt-filter-card">
          <div
            className="ms-2 mt-4"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: 1,
              maxWidth: "420px",
              border: "1px solid rgba(209, 213, 219, 1)",
              borderRadius: "38px",
              padding: "4px 8px 4px 12px",
              background: "#fff",
            }}
          >
            <SearchIcon
              size={16}
              color="#6c757d"
              style={{ flexShrink: 0 }}
            />

            <Form.Control
              placeholder="Search invoices"
              aria-label="Search invoices"
              value={filters.search}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, search: e.target.value }));
                setActivePage(1);
              }}
              style={{
                border: "none",
                boxShadow: "none",
                padding: "4px 0",
                fontSize: "14px",
                background: "transparent",
              }}
            />

            <button
              type="button"
              style={{
                border: "none",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                backgroundColor: "#2F7F8F",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                cursor: "pointer",
              }}
            >
              <SearchIcon
                size={16}
                color="#fff"
              />
            </button>
          </div>

          {/* FILTERS ROW */}
          <div className="d-flex flex-wrap align-items-center gap-2 ms-2 mt-2">
            {/* Time Range */}
            <div className="bt-dropdown-block ms-2" style={{ flex: '0 0 auto' }}>
              <span className="bt-dropdown-label bill-c" style={{ fontSize: '12px' }}>Time Range</span>
              <div className="bt-time-pills" style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                {[["1 mo", "1mo"], ["3 mo", "3mo"], ["1 yr", "1yr"]].map(([label, val]) => (
                  <button
                    key={val}
                    className={`bt-pill bill-height ${timeRange === val ? "bt-pill-active" : ""}`}
                    style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      borderRadius: '20px',
                      border: timeRange === val ? '1px solid #2f7f8f' : '1px solid #ddd',
                      background: timeRange === val ? '#2f7f8f' : 'transparent',
                      color: timeRange === val ? '#fff' : '#333',
                      cursor: 'pointer',
                      height: '30px'
                    }}
                    onClick={() => {
                      setTimeRange(val);
                      setFilters(prev => ({
                        ...prev,
                        timeRange: val,
                        startDate: "",
                        endDate: ""
                      }));
                      setActivePage(1);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Start Date */}
            <div className="bt-date-block ms-2" style={{ flex: '0 0 auto' }}>
              <span className="bt-dropdown-label bill-c" style={{ fontSize: '12px' }}>Start</span>
              <input
                type="date"
                value={filters.startDate}
                className="bt-date-input bill-height"
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  width: '135px',
                  borderRadius: '50px',
                  border: '1px solid #ddd',
                  height: '30px',
                  marginTop: '2px'
                }}
                onChange={(e) => {
                  setFilters(prev => ({
                    ...prev,
                    startDate: e.target.value,
                    timeRange: ""
                  }));
                  setTimeRange("");
                  setActivePage(1);
                }}
              />
            </div>

            {/* End Date */}
            <div className="bt-date-block" style={{ flex: '0 0 auto' }}>
              <span className="bt-dropdown-label bill-c" style={{ fontSize: '12px' }}>End</span>
              <input
                type="date"
                className="bt-date-input bill-height"
                value={filters.endDate}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  width: '135px',
                  borderRadius: '50px',
                  border: '1px solid #ddd',
                  height: '30px',
                  marginTop: '2px'
                }}
                onChange={(e) => {
                  setFilters(prev => ({
                    ...prev,
                    endDate: e.target.value,
                    timeRange: ""
                  }));
                  setTimeRange("");
                  setActivePage(1);
                }}
              />
            </div>

            {/* Customer Dropdown */}
            <div className="bt-dropdown-block ms-2" style={{ flex: '1', minWidth: '130px' }}>
              <span className="bt-dropdown-label bill-c" style={{ fontSize: '12px' }}>Customer</span>
              <div className="bt-select-wrap" style={{ position: 'relative', marginTop: '2px' }}>
                <select
                  className="bt-select bill-height"
                  style={{
                    padding: '4px 28px 4px 10px',
                    fontSize: '12px',
                    width: '100%',
                    borderRadius: '50px',
                    border: '1px solid #ddd',
                    height: '30px',
                    background: '#fff',
                    appearance: 'none',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  value={filters.customer}
                  onChange={(e) => {
                    setFilters(prev => ({
                      ...prev,
                      customer: e.target.value
                    }));
                    setActivePage(1);
                  }}
                >
                  <option value="All">All</option>
                  {customers.map(c => (
                    <option key={c._id || c.id} value={c.fullName || c.full_name || c.name || ""}>
                      {c.fullName || c.full_name || c.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="bt-select-chevron" style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6c757d'
                }} />
              </div>
            </div>

            {/* Status Dropdown */}
            <div className="bt-dropdown-block" style={{ flex: '1', minWidth: '130px' }}>
              <span className="bt-dropdown-label bill-c" style={{ fontSize: '12px' }}>Status</span>
              <div className="bt-select-wrap" style={{ position: 'relative', marginTop: '2px' }}>
                <select
                  className="bt-select bill-height"
                  style={{
                    padding: '4px 28px 4px 10px',
                    fontSize: '12px',
                    width: '100%',
                    borderRadius: '50px',
                    border: '1px solid #ddd',
                    height: '30px',
                    background: '#fff',
                    appearance: 'none',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  value={filters.status}
                  onChange={(e) => {
                    setFilters(prev => ({
                      ...prev,
                      status: e.target.value
                    }));
                    setActivePage(1);
                  }}
                >
                  <option value="All">All</option>
                  <option value="unbilled">Unbilled</option>
                  <option value="overdue">Overdue</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
                <ChevronDown size={14} className="bt-select-chevron" style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6c757d'
                }} />
              </div>
            </div>

            {/* Plan Dropdown */}
            <div className="bt-dropdown-block" style={{ flex: '1', minWidth: '130px' }}>
              <span className="bt-dropdown-label bill-c" style={{ fontSize: '12px' }}>Plan</span>
              <div className="bt-select-wrap" style={{ position: 'relative', marginTop: '2px' }}>
                <select
                  className="bt-select bill-height"
                  style={{
                    padding: '4px 28px 4px 10px',
                    fontSize: '12px',
                    width: '100%',
                    borderRadius: '50px',
                    border: '1px solid #ddd',
                    height: '30px',
                    background: '#fff',
                    appearance: 'none',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  value={filters.plan}
                  onChange={(e) => {
                    setFilters(prev => ({
                      ...prev,
                      plan: e.target.value
                    }));
                    setActivePage(1);
                  }}
                >
                  <option value="All">All</option>
                  {plans.map(p => (
                    <option key={p.id} value={p.plan_name || p.name}>
                      {p.plan_name || p.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="bt-select-chevron" style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6c757d'
                }} />
              </div>
            </div>

            {/* Reset Button */}
            <div
              className="d-flex align-items-center"
              style={{
                cursor: "pointer",
                padding: '4px 14px',
                borderRadius: '50px',
                height: '30px',
                marginTop: '18px',
                alignSelf: 'flex-end'
              }}
              onClick={resetFilters}
              title="Reset Filters"
            >
              <img
                src="/images/filter.svg"
                alt="Reset Filters"
                style={{ width: '14px', height: '14px' }}
              />
              <span className="ms-1 text-muted small fw-medium" style={{ fontSize: '12px' }}>Reset</span>
            </div>
          </div>

          {/* TABLE */}
          <table className="bt-table mt-4">
            <thead>
              <tr>
                <th onClick={() => handleSort("invoice_number")}>
                  Invoice ID <SortIcon column="invoice_number" sortColumn={sortColumn} sortDirection={sortDirection} />
                </th>
                <th onClick={() => handleSort("customer")}>
                  Customer <SortIcon column="customer" sortColumn={sortColumn} sortDirection={sortDirection} />
                </th>
                <th onClick={() => handleSort("plan")}>
                  Plan <SortIcon column="plan" sortColumn={sortColumn} sortDirection={sortDirection} />
                </th>
                <th onClick={() => handleSort("billing_period")}>
                  Billing Period <SortIcon column="billing_period" sortColumn={sortColumn} sortDirection={sortDirection} />
                </th>
                <th onClick={() => handleSort("amount")}>
                  Amount <SortIcon column="amount" sortColumn={sortColumn} sortDirection={sortDirection} />
                </th>
                <th onClick={() => handleSort("status")}>
                  Status <SortIcon column="status" sortColumn={sortColumn} sortDirection={sortDirection} />
                </th>
                <th onClick={() => handleSort("issue_date")}>
                  Invoice Date <SortIcon column="issue_date" sortColumn={sortColumn} sortDirection={sortDirection} />
                </th>
                <th onClick={() => handleSort("due_date")}>
                  Due Date <SortIcon column="due_date" sortColumn={sortColumn} sortDirection={sortDirection} />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">Loading billing data...</td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">No records found.</td>
                </tr>
              ) : (
                data.map((row, i) => (
                  <tr key={row.id || i}>
                    <td>
                      <span
                        style={{ color: "#2F7A87", textDecoration: "underline", cursor: "pointer" }}
                        onClick={() => handleView(row)}
                      >
                        {row.invoice_number}
                      </span>
                    </td>
                    <td className="bt-td-bold">{row.customer || "-"}</td>
                    <td>{row.plan || "-"}</td>
                    <td>
                      {row.issue_date && row.due_date
                        ? `${formatDate(row.issue_date)} - ${formatDate(row.due_date)}`
                        : "-"}
                    </td>
                    <td>₹ {Number(row.amount || 0).toLocaleString()}</td>
                    <td>
                      <span className={`bt-status-${(row.status || "unbilled").toLowerCase()} ${isOverdue(row.due_date) && row.status !== 'paid' ? 'bt-status-overdue' : ''}`}>
                        {!row.status ? 'Unbilled' : (isOverdue(row.due_date) && row.status !== 'paid' ? 'Overdue' : row.status)}
                      </span>
                    </td>
                    <td>{formatDate(row.issue_date)}</td>
                    <td>{formatDate(row.due_date)}</td>
                    <td>
                      <div className="bt-actions" style={{ position: "relative", display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Eye
                          size={18}
                          color="#64748b"
                          className="bt-eye-icon"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleView(row)}
                        />
                        <button
                          className="bt-dots-btn"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === row.id ? null : row.id);
                          }}
                        >
                          <MoreVertical size={18} color="#64748b" />
                        </button>
                        {openMenuId === row.id && (
                          <div className="bt-dropdown-menu" style={{
                            position: 'absolute',
                            top: '100%',
                            right: '0',
                            background: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            minWidth: '160px',
                            padding: '8px 0'
                          }}>
                            <div className="bt-dropdown-item" onClick={() => handleEdit(row)} style={{ padding: '6px 16px', cursor: 'pointer', fontSize: '14px', color: '#212529' }}>Edit</div>
                            <div className="bt-dropdown-item" onClick={() => handleDelete(row.id)} style={{ padding: '6px 16px', cursor: 'pointer', fontSize: '14px', color: '#dc3545' }}>Delete</div>
                            <div className="bt-dropdown-item" onClick={() => handleDownloadInvoice(row)} style={{ padding: '6px 16px', cursor: 'pointer', fontSize: '14px', color: '#212529' }}>Download Invoice</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="table-footer gap-3">
          <span>
            {total === 0
              ? "No records"
              : `Showing ${start}-${end} of ${total}`}
          </span>

          <div className="ms-5">
            <ResultsPagination
              activePage={activePage}
              setActivePage={setActivePage}
              perPage={perPage}
              setPerPage={setPerPage}
              totalPages={totalPages}
              perPageOptions={[5, 10, 20, 50]}
            />
          </div>
        </div>
      </div>

      {/* Add New Invoice Modal */}
      <Modal show={showAddInvoiceModal} onHide={() => setShowAddInvoiceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleNewInvoiceSubmit}>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="customerSelect">Customer</Form.Label>
              <Form.Select
                id="customerSelect"
                name="user_id"
                value={newInvoiceData.user_id}
                onChange={handleNewInvoiceChange}
                required
              >
                <option value="">Select Customer</option>
                {customers.map(c => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.fullName || c.full_name || c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="planSelect">Subscription Plan</Form.Label>
              <Form.Select
                id="planSelect"
                name="plan_id"
                value={newInvoiceData.plan_id}
                onChange={handleNewInvoiceChange}
                required
              >
                <option value="">Select Plan</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.plan_name || p.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="amountInput">Amount</Form.Label>
              <Form.Control
                type="number"
                id="amountInput"
                name="amount"
                value={newInvoiceData.amount}
                onChange={handleNewInvoiceChange}
                step="0.01"
                min="0.01"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="dueDateInput">Due Date</Form.Label>
              <Form.Control
                type="date"
                id="dueDateInput"
                name="due_date"
                value={newInvoiceData.due_date}
                onChange={handleNewInvoiceChange}
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
              <button
                type="button"
                style={{
                  background: "transparent",
                  border: "1px solid #2F7A87",
                  color: "#2F7A87",
                  textDecoration: "none",
                  padding: "6px 16px",
                  borderRadius: "50px",
                  fontWeight: "500"
                }}
                onClick={() => setShowAddInvoiceModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  background: "#2F7A87",
                  border: "none",
                  color: "#fff",
                  textDecoration: "none",
                  padding: "6px 16px",
                  borderRadius: "50px",
                  fontWeight: "500"
                }}
              >
                Add Invoice
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditInvoiceSubmit}>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="editAmountInput">Amount</Form.Label>
              <Form.Control
                type="number"
                id="editAmountInput"
                name="amount"
                value={editInvoiceData.amount}
                onChange={handleEditInvoiceChange}
                step="0.01"
                min="0.01"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="editDueDateInput">Due Date</Form.Label>
              <Form.Control
                type="date"
                id="editDueDateInput"
                name="due_date"
                value={editInvoiceData.due_date}
                onChange={handleEditInvoiceChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="editStatusSelect">Status</Form.Label>
              <Form.Select
                id="editStatusSelect"
                name="status"
                value={editInvoiceData.status}
                onChange={handleEditInvoiceChange}
                required
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Form.Group>

            <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
              <button
                type="button"
                style={{
                  background: "transparent",
                  border: "1px solid #2F7A87",
                  color: "#2F7A87",
                  textDecoration: "none",
                  padding: "6px 16px",
                  borderRadius: "50px",
                  fontWeight: "500"
                }}
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  background: "#2F7A87",
                  border: "none",
                  color: "#fff",
                  textDecoration: "none",
                  padding: "6px 16px",
                  borderRadius: "50px",
                  fontWeight: "500"
                }}
              >
                OK
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setInvoiceToDelete(null);
        }}
        centered
        className="smt-theme"
      >
        <Modal.Header closeButton style={{ borderBottom: "none" }}>
          <Modal.Title style={{ color: "#2F7A87" }}>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pb-4">
          <h5 className="mb-4">Are you sure you want to delete this invoice?</h5>
          <div className="d-flex justify-content-center gap-3">
            <button
              type="button"
              style={{
                background: "transparent",
                border: "1px solid #2F7A87",
                color: "#2F7A87",
                textDecoration: "none",
                padding: "6px 16px",
                borderRadius: "50px",
                fontWeight: "500"
              }}
              onClick={() => {
                setShowDeleteModal(false);
                setInvoiceToDelete(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{
                background: "#2F7A87",
                border: "none",
                color: "#fff",
                textDecoration: "none",
                padding: "6px 16px",
                borderRadius: "50px",
                fontWeight: "500"
              }}
              onClick={confirmDeleteInvoice}
            >
              Delete Invoice
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
